// Client-side tracking helper para el directorio SEO-first. Reemplaza
// el `track()` de `src/lib/analytics.ts` en el modelo actual. Envía a
// `POST /api/track/directory`. Ver docs/tracking-plan.md.
//
// - GA4 sigue haciendo pageviews + acquisition. Este helper es solo
//   para lo que GA4 no da bien (interacciones específicas, cobertura,
//   errores).
// - `visitorId` en localStorage (retención cross-session).
// - `sessionId` en sessionStorage (una sesión de navegador).
// - Dedup client-side ventana 5 s por `(eventType, dedupKey)` — evita
//   duplicados al montar/re-montar componentes.
// - Fire-and-forget con `sendBeacon`, fallback a `fetch({keepalive:true})`.

import { classifyChannel, parseUtms } from "@/lib/analytics/traffic-channel";

// ── Whitelist de eventos ────────────────────────────────────────────
// Cambios en esta lista requieren update en `/api/track/directory` +
// dashboard `/gestion/directorio/analytics`. Ver plan.
export const DIRECTORY_EVENT_TYPES = [
  // Tráfico. Antes se delegaba a GA4, pero sin un page_view propio no
  // hay denominador para ningún porcentaje por canal: no se puede
  // decir "la publicidad trae X% del tráfico" sin contar el tráfico.
  "page_view",
  // Embudo de descubrimiento: ¿la gente interactúa con las listas o
  // rebota en el home? Sin estos dos no se puede saber dónde se pierde
  // el tráfico entre "llegó" y "contactó".
  "card_click",
  "comercio_view",
  "scroll_depth",
  // Ficha del comercio (comportamiento)
  "whatsapp_click",
  "phone_call_click",
  "external_link_click",
  "menu_family_view",
  // Discovery / cobertura del catálogo
  "search_query",
  "hub_empty_view",
  // Salud operativa
  "js_error",
  "web_vitals",
  "not_found_view",
  // Embudo de la TIENDA (`/{slug}`). Hasta ahora el storefront emitía
  // a `@/lib/analytics`, que es un no-op desde una migración vieja: no
  // quedaba registro de nada entre "entró" y "pidió". Sin esto, una
  // tienda con visitas y cero pedidos no se puede diagnosticar — no se
  // sabe si nadie quiso o si algo no dejó.
  "view_product",
  "add_to_cart",
  "remove_from_cart",
  "begin_checkout",
  // Pedido concretado: `purchase` en tiendas con gestión,
  // `pedido_wa_disparado` en las de WhatsApp (son entidades distintas,
  // Order vs PedidoWhatsapp).
  "purchase",
  "pedido_wa_disparado",
  // El envío que falló. Es el dato que más falta cuando el embudo se
  // corta en el último paso.
  "checkout_error",
] as const;

export type DirectoryEventType = (typeof DIRECTORY_EVENT_TYPES)[number];

export const PAGE_TYPES = [
  "home",
  "partido",
  "localidad",
  "categoria",
  "ficha",
  "search",
  // Storefront de una tienda (`/{slug}`). Antes caía en "partido" por
  // tener un solo segmento, así que sus page_view se mezclaban con los
  // de los hubs.
  "tienda",
  "other",
] as const;

export type PageType = (typeof PAGE_TYPES)[number];

// ── Payload ─────────────────────────────────────────────────────────

export type TrackOptions = {
  comercioId?: number | null;
  /** Tienda que originó el evento (storefront). Independiente de
   *  `comercioId`: una tienda puede no tener ficha en el directorio. */
  merchantId?: number | null;
  pageType?: PageType;
  path?: string;
  // dedupKey opcional para desambiguar eventos del mismo tipo en la
  // misma página (ej. dos WhatsApp buttons: sticky vs inline).
  dedupKey?: string;
  metadata?: Record<string, unknown>;
};

type Envelope = {
  eventType: DirectoryEventType;
  visitorId: string;
  sessionId: string;
  comercioId: number | null;
  merchantId: number | null;
  pageType: PageType | null;
  path: string | null;
  metadata: Record<string, unknown> | null;
  // Atribución de primer toque. Se resuelve una vez por sesión y viaja
  // en todos los eventos, para poder cruzar cualquier métrica por canal
  // sin joins.
  attribution: Attribution | null;
};

export type Attribution = {
  channel: string;
  referrer: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  landingPath: string | null;
  /** Query string crudo del landing — el servidor lo necesita para
   *  detectar fbclid/igshid al re-derivar el canal. */
  landingQuery: string | null;
  isNewVisitor: boolean;
};

// ── IDs persistentes ────────────────────────────────────────────────

const VID_KEY = "_dvid";
const SID_KEY = "_dsid";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback para navegadores muy viejos (poco probable — Next 16
  // exige runtimes modernos, pero por si acaso).
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  try {
    let v = window.localStorage.getItem(VID_KEY);
    if (!v) {
      v = uuid();
      window.localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch {
    return "";
  }
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let s = window.sessionStorage.getItem(SID_KEY);
    if (!s) {
      s = uuid();
      window.sessionStorage.setItem(SID_KEY, s);
    }
    return s;
  } catch {
    return "";
  }
}

// ── Atribución de primer toque ──────────────────────────────────────
//
// Se resuelve UNA vez, al abrir la sesión, y se cachea en
// `sessionStorage`. Si se recalculara en cada evento, el referrer ya
// sería interno (pedile.com) después de la primera navegación y todo
// terminaría clasificado como `direct`.

const ATTR_KEY = "_dattr";
const SEEN_KEY = "_dseen";

function computeAttribution(): Attribution {
  const qs = typeof location !== "undefined" ? location.search : "";
  const ref = typeof document !== "undefined" ? document.referrer || null : null;
  const { utmSource, utmMedium, utmCampaign } = parseUtms(qs);

  // Primera sesión de este visitante. Se marca en localStorage: la
  // métrica que responde "¿la publicidad me trae gente nueva?" es
  // visitantes nuevos por canal, no sesiones totales — alguien que
  // vuelve entra como `direct` aunque lo haya traído un ad.
  let isNewVisitor = false;
  try {
    if (!window.localStorage.getItem(SEEN_KEY)) {
      isNewVisitor = true;
      window.localStorage.setItem(SEEN_KEY, "1");
    }
  } catch {
    // storage bloqueado — se asume visitante recurrente para no inflar
    // el conteo de nuevos.
  }

  return {
    channel: classifyChannel({
      referrer: ref,
      utmSource,
      utmMedium,
      queryString: qs,
      selfHost: typeof location !== "undefined" ? location.hostname : null,
    }),
    referrer: ref ? ref.slice(0, 500) : null,
    utmSource,
    utmMedium,
    utmCampaign,
    landingPath:
      typeof location !== "undefined" ? location.pathname.slice(0, 500) : null,
    landingQuery: qs ? qs.slice(0, 500) : null,
    isNewVisitor,
  };
}

function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = window.sessionStorage.getItem(ATTR_KEY);
    if (cached) return JSON.parse(cached) as Attribution;
    const attr = computeAttribution();
    window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    // Sin sessionStorage no se puede fijar el primer toque; se computa
    // igual para no perder el dato del landing.
    try {
      return computeAttribution();
    } catch {
      return null;
    }
  }
}

// ── Dedup client-side ───────────────────────────────────────────────

const DEDUP_WINDOW_MS = 5_000;
const lastFired = new Map<string, number>();

function shouldDedup(eventType: string, dedupKey: string | null): boolean {
  const key = `${eventType}::${dedupKey ?? ""}`;
  const now = Date.now();
  const prev = lastFired.get(key);
  if (prev != null && now - prev < DEDUP_WINDOW_MS) return true;
  lastFired.set(key, now);
  return false;
}

// ── Send ────────────────────────────────────────────────────────────

function send(envelope: Envelope): void {
  const body = JSON.stringify(envelope);
  const url = "/api/track/directory";
  try {
    if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch {
    // fallthrough al fetch
  }
  try {
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // silent
  }
}

// ── API pública ─────────────────────────────────────────────────────

/**
 * Emite un evento del directorio. No-op durante SSR (typeof window === "undefined").
 * Si el `eventType` no está en la whitelist, no se envía y se loguea un
 * warning en dev.
 */
export function track(
  eventType: DirectoryEventType,
  opts: TrackOptions = {},
): void {
  if (typeof window === "undefined") return;
  if (!DIRECTORY_EVENT_TYPES.includes(eventType)) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[directory-tracking] eventType desconocido: ${eventType}`);
    }
    return;
  }
  if (shouldDedup(eventType, opts.dedupKey ?? null)) return;

  const envelope: Envelope = {
    eventType,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    comercioId: opts.comercioId ?? null,
    merchantId: opts.merchantId ?? null,
    pageType: opts.pageType ?? null,
    path: opts.path ?? (typeof location !== "undefined" ? location.pathname : null),
    metadata: opts.metadata ?? null,
    attribution: getAttribution(),
  };
  send(envelope);
}

// Helper para tests: reset del dedup + IDs. NO usar en producción.
export function __resetForTests(): void {
  lastFired.clear();
  try {
    window.sessionStorage.removeItem(ATTR_KEY);
  } catch {
    // no-op
  }
}
